import { useEffect, useMemo, useRef, useState } from "react";

import { ApiError, apiClient } from "../../api/client";
import type { CSVRowError, CoinUploadResponse, Season } from "../../api/types";
import { useIdempotencyKey } from "../../lib/idempotencyKey";

// Sample rows demo every supported combination of the three columns:
//   1. grant + new avatar
//   2. grant only, avatar left alone
//   3. onboard / set avatar without granting (coin_amount blank)
// The avatar URLs hit cataas.com/cat/<id>?type=square — a public service
// that returns the same square-cropped cat image for a given id, so the
// template renders consistently every time someone downloads it.
const csvTemplateBody =
  "email,coin_amount,avatar_url\n" +
  "ada.lovelace@queen.one,50,https://cataas.com/cat/K2tkCwJsYodPFVyq?type=square\n" +
  "grace.hopper@queen.one,75,\n" +
  "margaret.hamilton@queen.one,,https://cataas.com/cat/FSHFVqdjj9f24v0U?type=square\n";
const csvTemplateDataURI = `data:text/csv;charset=utf-8,${encodeURIComponent(csvTemplateBody)}`;

interface State {
  filename: string;
  fileSize: number;
  csv: string;
  submitting: boolean;
  result: CoinUploadResponse | null;
  errorMessage: string | null;
  rowErrors: CSVRowError[];
}

const initial: State = {
  filename: "",
  fileSize: 0,
  csv: "",
  submitting: false,
  result: null,
  errorMessage: null,
  rowErrors: []
};

interface ActiveSeasonState {
  status: "loading" | "ready" | "error";
  season: Season | null;
}

export function CoinUploads() {
  const [state, setState] = useState<State>(initial);
  const [dragActive, setDragActive] = useState(false);
  const [activeSeason, setActiveSeason] = useState<ActiveSeasonState>({ status: "loading", season: null });
  // dragenter/dragleave fire on every nested element, so a bare boolean
  // toggles off when the cursor crosses a child boundary. The counter
  // tracks net depth so we only reset when the cursor truly leaves.
  const dragDepthRef = useRef(0);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const { keyFor, reset } = useIdempotencyKey();

  // Reset on unmount so a future visit starts with a fresh idempotency key.
  useEffect(() => () => reset(), [reset]);

  // Surface the season this upload will target so the user doesn't have to
  // guess which one is live.
  useEffect(() => {
    let cancelled = false;
    apiClient
      .listSeasons({ status: "active", limit: 1 })
      .then((response) => {
        if (cancelled) {
          return;
        }
        setActiveSeason({ status: "ready", season: response.seasons[0] ?? null });
      })
      .catch(() => {
        if (cancelled) {
          return;
        }
        setActiveSeason({ status: "error", season: null });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const previewRows = useMemo(() => parsePreview(state.csv), [state.csv]);

  async function ingestFile(file: File) {
    if (!isAcceptableCSV(file)) {
      setState((previous) => ({
        ...previous,
        errorMessage: `"${file.name}" is not a CSV file.`,
        result: null,
        rowErrors: []
      }));
      return;
    }
    const text = await file.text();
    setState((previous) => ({
      ...previous,
      filename: file.name,
      fileSize: file.size,
      csv: text,
      result: null,
      rowErrors: [],
      errorMessage: null
    }));
    reset();
  }

  function handleFileInput(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (file === undefined) {
      return;
    }
    void ingestFile(file);
    // Allow re-selecting the same file after a remove — the input keeps the
    // last value otherwise and onChange wouldn't fire.
    event.target.value = "";
  }

  function handleDragEnter(event: React.DragEvent<HTMLLabelElement>) {
    event.preventDefault();
    if (!hasFiles(event.dataTransfer)) {
      return;
    }
    dragDepthRef.current += 1;
    setDragActive(true);
  }

  function handleDragOver(event: React.DragEvent<HTMLLabelElement>) {
    // preventDefault on dragover is what tells the browser this element is
    // a valid drop target; without it, drop never fires.
    event.preventDefault();
    if (event.dataTransfer.dropEffect !== "copy") {
      event.dataTransfer.dropEffect = "copy";
    }
  }

  function handleDragLeave(event: React.DragEvent<HTMLLabelElement>) {
    event.preventDefault();
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) {
      setDragActive(false);
    }
  }

  function handleDrop(event: React.DragEvent<HTMLLabelElement>) {
    event.preventDefault();
    dragDepthRef.current = 0;
    setDragActive(false);
    const file = event.dataTransfer.files?.[0];
    if (file === undefined) {
      return;
    }
    void ingestFile(file);
  }

  function handleClear() {
    setState({ ...initial });
    reset();
    if (fileInputRef.current !== null) {
      fileInputRef.current.value = "";
    }
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (state.submitting || state.csv.trim() === "") {
      return;
    }
    setState((previous) => ({ ...previous, submitting: true, errorMessage: null, rowErrors: [] }));

    const payload = { filename: state.filename, csv: state.csv };
    const idempotencyKey = keyFor(JSON.stringify(payload));

    try {
      const result = await apiClient.uploadCoins(payload, idempotencyKey);
      setState((previous) => ({ ...previous, submitting: false, result, errorMessage: null }));
    } catch (caught) {
      if (caught instanceof ApiError && caught.code === "csv_invalid") {
        const rows = caught.body?.errors;
        setState((previous) => ({
          ...previous,
          submitting: false,
          rowErrors: Array.isArray(rows) ? rows : [],
          errorMessage: "CSV failed validation. See per-row errors below."
        }));
        return;
      }
      setState((previous) => ({ ...previous, submitting: false, errorMessage: messageFor(caught) }));
    }
  }

  const hasFile = state.filename !== "" && state.csv !== "";

  return (
    <div className="card">
      <h2>Upload coins</h2>
      <div className="muted upload-help">
        <p>
          Columns: <code>email</code>, <code>coin_amount</code>, and an optional <code>avatar_url</code>.
          Uploads are all-or-nothing — any invalid row rejects the whole file.
          {" "}
          <a href={csvTemplateDataURI} download="coin-grants-template.csv">Download a sample CSV</a>.
        </p>
        <ul className="upload-help__list">
          <li>
            <strong><code>coin_amount</code></strong> — non-negative integer.
            Leave blank or set to <code>0</code> to onboard a user without granting coins.
          </li>
          <li>
            <strong><code>avatar_url</code></strong> — optional <code>https://</code> link
            to an image. Useful for users whose Google account doesn't have a photo.
            Leave blank to keep the existing avatar; a non-empty value overwrites
            whatever's there (a later sign-in will overwrite again from the SSO provider).
            Need a placeholder for testing? <code>https://cataas.com/cat/&lt;id&gt;?type=square</code> returns a
            stable square cat image — find an id at <a
              href="https://cataas.com/cat?json=true"
              target="_blank"
              rel="noreferrer"
            >cataas.com/cat?json=true</a>.
          </li>
        </ul>
      </div>
      <p className="muted">
        {activeSeason.status === "loading" && <>Looking up the active season…</>}
        {activeSeason.status === "error" && <>Couldn't reach the seasons API.</>}
        {activeSeason.status === "ready" && activeSeason.season !== null && (
          <>Targets the active season <strong>{activeSeason.season.name}</strong>.</>
        )}
        {activeSeason.status === "ready" && activeSeason.season === null && (
          <>No active season — activate one in Seasons before uploading.</>
        )}
      </p>

      <form onSubmit={handleSubmit}>
        <label
          className={`dropzone${dragActive ? " is-active" : ""}${hasFile ? " has-file" : ""}`}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            className="dropzone__input"
            onChange={handleFileInput}
          />
          {hasFile ? (
            <div className="dropzone__file">
              <CSVIcon />
              <div className="dropzone__file-text">
                <strong>{state.filename}</strong>
                <span>{formatBytes(state.fileSize)} · {previewRows.length} row{previewRows.length === 1 ? "" : "s"}</span>
              </div>
              <button
                type="button"
                className="btn btn--ghost dropzone__clear"
                onClick={(event) => {
                  // Stop the click from bubbling to the label and re-opening
                  // the file picker.
                  event.preventDefault();
                  event.stopPropagation();
                  handleClear();
                }}
              >
                Replace
              </button>
            </div>
          ) : (
            <div className="dropzone__prompt">
              <UploadIcon />
              <strong>{dragActive ? "Drop the CSV here" : "Drag & drop a CSV"}</strong>
              <span className="muted">or click to choose a file</span>
            </div>
          )}
        </label>

        {previewRows.length > 0 && (() => {
          const hasAvatar = previewRows.some((row) => row.avatarURL !== "");
          return (
            <div className="upload-preview">
              <h3>Preview ({previewRows.length} row{previewRows.length === 1 ? "" : "s"})</h3>
              <table>
                <thead>
                  <tr>
                    <th>email</th>
                    <th>coin_amount</th>
                    {hasAvatar && <th>avatar_url</th>}
                  </tr>
                </thead>
                <tbody>
                  {previewRows.slice(0, 10).map((row, index) => (
                    <tr key={`${row.email}-${index}`}>
                      <td>{row.email}</td>
                      <td>{row.amount}</td>
                      {hasAvatar && (
                        <td>
                          {row.avatarURL !== "" ? (
                            <img
                              src={row.avatarURL}
                              alt=""
                              aria-hidden="true"
                              className="typeahead__avatar typeahead__avatar--img"
                            />
                          ) : (
                            <span className="muted">—</span>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
              {previewRows.length > 10 && (
                <p className="muted">…and {previewRows.length - 10} more rows.</p>
              )}
            </div>
          );
        })()}
        {state.errorMessage !== null && <p className="form-row__error">{state.errorMessage}</p>}
        {state.rowErrors.length > 0 && (
          <table className="upload-errors">
            <thead>
              <tr>
                <th>Line</th>
                <th>Email</th>
                <th>Reason</th>
              </tr>
            </thead>
            <tbody>
              {state.rowErrors.map((row, index) => (
                <tr key={index}>
                  <td>{row.line}</td>
                  <td>{row.email ?? "—"}</td>
                  <td>{row.message}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {state.result !== null && (
          <p className="success">
            {state.result.upload.id === ""
              ? "Users onboarded. No coins granted."
              : `Imported ${state.result.upload.row_count} coin grant${state.result.upload.row_count === 1 ? "" : "s"}.`}
          </p>
        )}
        <div className="modal__footer">
          <button
            type="submit"
            className="btn btn--primary"
            disabled={state.submitting || !hasFile}
          >
            {state.submitting ? "Uploading…" : "Upload coins"}
          </button>
        </div>
      </form>
    </div>
  );
}

function UploadIcon() {
  return (
    <svg
      className="dropzone__icon"
      width="36"
      height="36"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 16V4" />
      <path d="m6 10 6-6 6 6" />
      <path d="M4 16v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3" />
    </svg>
  );
}

function CSVIcon() {
  return (
    <svg
      className="dropzone__icon"
      width="32"
      height="32"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6" />
      <path d="M8 13h8" />
      <path d="M8 17h5" />
    </svg>
  );
}

function isAcceptableCSV(file: File): boolean {
  if (file.name.toLowerCase().endsWith(".csv")) {
    return true;
  }
  // Some browsers report text/csv, application/vnd.ms-excel, or empty for
  // CSV — defer to the extension as the primary signal.
  return file.type === "text/csv";
}

function hasFiles(transfer: DataTransfer): boolean {
  // dataTransfer.types is a DOMStringList in some browsers; .includes works
  // on both array-like shapes.
  return Array.from(transfer.types).includes("Files");
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function parsePreview(csv: string): { email: string; amount: string; avatarURL: string }[] {
  if (csv.trim() === "") {
    return [];
  }
  const lines = csv.split(/\r?\n/).filter((line) => line.trim() !== "");
  if (lines.length < 2) {
    return [];
  }
  const headerCells = (lines[0] ?? "").split(",").map((cell) => cell.trim().toLowerCase());
  const emailCol = headerCells.indexOf("email");
  const amountCol = headerCells.findIndex((cell) => cell === "coin_amount" || cell === "amount");
  const avatarCol = headerCells.indexOf("avatar_url");
  if (emailCol === -1 || amountCol === -1) {
    return [];
  }
  return lines.slice(1).map((line) => {
    const cells = line.split(",");
    return {
      email: (cells[emailCol] ?? "").trim(),
      amount: (cells[amountCol] ?? "").trim(),
      avatarURL: avatarCol === -1 ? "" : (cells[avatarCol] ?? "").trim()
    };
  });
}

function messageFor(caught: unknown): string {
  if (caught instanceof ApiError) {
    return caught.message;
  }
  return caught instanceof Error ? caught.message : "Upload failed.";
}
